import React, { useState, useEffect, useRef } from 'react';
import {
  CreateStudentDemandInput,
  StudentDemandRecord,
  Weekday,
} from '../../../types';
import {
  PREDEFINED_ALEXANDRIA_AREAS,
  ALEXANDRIA_AREA_GROUPS,
  AlexandriaArea,
  WEEKDAY_OPTIONS,
} from '../../../constants/alexandriaAreas';
import { SEED_UNIVERSITIES } from '../../../constants/mockSeed';
import { studentDemandService } from '../../../services/studentDemandService';
import { useAuth } from '../../../auth/AuthContext';
import {
  UserPlus,
  Building2,
  MapPin,
  Phone,
  CheckCircle2,
  Calendar,
  AlertTriangle,
  RotateCcw,
  Sparkles,
  CalendarDays,
} from 'lucide-react';

import './StudentDemand.css';

interface DemandDataEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRecordCreated: (newRecord: StudentDemandRecord) => void;
  onOpenExistingRecord?: (record: StudentDemandRecord) => void;
  initialUniversityId?: string;
  initialUniversityName?: string;
  initialAreaId?: string;
  initialStudentName?: string;
  initialPhone?: string;
}

export const DemandDataEntryModal: React.FC<DemandDataEntryModalProps> = ({
  isOpen,
  onClose,
  onRecordCreated,
  onOpenExistingRecord,
  initialUniversityId,
  initialUniversityName,
  initialAreaId,
  initialStudentName,
  initialPhone,
}) => {
  const { user, studentProfile } = useAuth();

  // Form State
  const [studentName, setStudentName] = useState(
    initialStudentName || studentProfile?.fullName || ''
  );
  const [phone, setPhone] = useState(
    initialPhone || studentProfile?.phoneNumber || ''
  );
  const [selectedAreaId, setSelectedAreaId] = useState(initialAreaId || 'alex_east_smouha');
  
  // University Autocomplete & Dynamic Entry State
  const [universityInput, setUniversityInput] = useState(initialUniversityName || '');
  const [selectedUniversityId, setSelectedUniversityId] = useState(initialUniversityId || '');
  const [universityGovernorate, setUniversityGovernorate] = useState('');
  const [showUniDropdown, setShowUniDropdown] = useState(false);
  const [knownUniversities, setKnownUniversities] = useState<
    Array<{ id: string; name: string; governorate: string }>
  >(() => {
    return SEED_UNIVERSITIES.map((u) => ({
      id: u.id,
      name: u.name,
      governorate: u.governorate,
    }));
  });

  const [attendanceDays, setAttendanceDays] = useState<string>('16');
  const [selectedDays, setSelectedDays] = useState<Weekday[]>([
    'SATURDAY',
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
  ]);
  const [departureTime, setDepartureTime] = useState<string>('06:30');
  const [returnTime, setReturnTime] = useState<string>('16:00');
  const [notes, setNotes] = useState('');

  const toggleTravelDay = (day: Weekday) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  // Custom Area Workflow ("منطقة أخرى")
  const [isCustomArea, setIsCustomArea] = useState(false);
  const [customAreaName, setCustomAreaName] = useState('');
  const [customAreaGroup, setCustomAreaGroup] = useState('group_other');

  // Sticky fields toggle (fast data entry)
  const [keepSelectionsSticky, setKeepSelectionsSticky] = useState(true);

  // Status & Validation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [duplicateWarning, setDuplicateWarning] = useState<StudentDemandRecord | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  // Area Search
  const [areaSearch, setAreaSearch] = useState('');

  // Refs
  const nameInputRef = useRef<HTMLInputElement>(null);
  const uniDropdownRef = useRef<HTMLDivElement>(null);

  // Load known universities including those previously registered in studentDemand
  useEffect(() => {
    let isMounted = true;
    const fetchKnownUnis = async () => {
      try {
        const records = await studentDemandService.getAllDemandRecords();
        if (!isMounted) return;
        const map = new Map<string, { id: string; name: string; governorate: string }>();

        // Add seeds first
        SEED_UNIVERSITIES.forEach((u) => {
          map.set(u.name.trim().toLowerCase(), {
            id: u.id,
            name: u.name.trim(),
            governorate: u.governorate,
          });
        });

        // Add universities already present in demand records
        records.forEach((r) => {
          if (r.universityNameSnapshot && r.universityNameSnapshot.trim()) {
            const key = r.universityNameSnapshot.trim().toLowerCase();
            if (!map.has(key)) {
              map.set(key, {
                id: r.universityId,
                name: r.universityNameSnapshot.trim(),
                governorate: r.universityGovernorateNameSnapshot || 'غير محدد',
              });
            }
          }
        });

        const list = Array.from(map.values());
        setKnownUniversities(list);

        // Initialize university if not set
        if (!universityInput) {
          const defaultUni =
            list.find((u) => u.id === (initialUniversityId || 'uni_cairo')) || list[0];
          if (defaultUni) {
            setUniversityInput(defaultUni.name);
            setSelectedUniversityId(defaultUni.id);
            setUniversityGovernorate(defaultUni.governorate);
          }
        }
      } catch {
        // Fallback to seeds
      }
    };

    if (isOpen) {
      fetchKnownUnis();
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen, initialUniversityId]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (uniDropdownRef.current && !uniDropdownRef.current.contains(e.target as Node)) {
        setShowUniDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Filtered Universities based on input text
  const cleanUniInput = universityInput.trim().toLowerCase();
  const filteredUniversities = knownUniversities.filter((u) =>
    cleanUniInput ? u.name.toLowerCase().includes(cleanUniInput) : true
  );

  const exactUniMatch = knownUniversities.find(
    (u) => u.name.trim().toLowerCase() === cleanUniInput
  );

  // Filtered Areas
  const filteredAreas: AlexandriaArea[] = PREDEFINED_ALEXANDRIA_AREAS.filter(
    (a) =>
      !areaSearch.trim() ||
      a.name.includes(areaSearch.trim()) ||
      a.groupName.includes(areaSearch.trim())
  );

  const resetStudentFields = () => {
    setStudentName('');
    setPhone('');
    setNotes('');
    setClientErrors({});
    setDuplicateWarning(null);
    if (!keepSelectionsSticky) {
      setAttendanceDays('16');
    }
    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 50);
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!studentName.trim() || studentName.trim().length < 2) {
      errors.studentName = 'يرجى إدخال اسم الطالب (حرفين على الأقل)';
    }

    const cleanPhone = phone.trim().replace(/\D/g, '');
    if (!cleanPhone) {
      errors.phone = 'يرجى إدخال رقم هاتف الطالب';
    } else if (cleanPhone.length !== 11 && !phone.startsWith('+20')) {
      errors.phone = 'رقم الهاتف يجب أن يتكون من 11 رقماً (مثال: 01012345678)';
    } else if (!/^(010|011|012|015)/.test(cleanPhone) && !cleanPhone.startsWith('201')) {
      errors.phone = 'يجب أن يبدأ رقم الهاتف بـ 010 أو 011 أو 012 أو 015';
    }

    if (isCustomArea && !customAreaName.trim()) {
      errors.customArea = 'يرجى كتابة اسم المنطقة السكنية';
    }

    if (!universityInput.trim() || universityInput.trim().length < 2) {
      errors.university = 'يرجى كتابة أو اختيار اسم الجامعة المطلوبة';
    }

    if (attendanceDays && attendanceDays.trim()) {
      const days = Number(attendanceDays);
      if (isNaN(days) || !Number.isInteger(days) || days < 1 || days > 31) {
        errors.attendanceDays = 'عدد أيام الحضور يجب أن يكون بين 1 و 31 يوماً';
      }
    }

    setClientErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (addAnother: boolean = false, forceDuplicate: boolean = false) => {
    if (!validate()) return;

    setIsSubmitting(true);
    setDuplicateWarning(null);

    try {
      const areaGroupObj = ALEXANDRIA_AREA_GROUPS.find((g) => g.id === customAreaGroup);

      // Resolve university: if match exists, use its id; otherwise generate a clean id
      const trimmedUniName = universityInput.trim();
      let uniId = selectedUniversityId;
      let gov = universityGovernorate.trim() || 'غير محدد';

      if (exactUniMatch) {
        uniId = exactUniMatch.id;
        gov = exactUniMatch.governorate;
      } else if (!uniId) {
        // Generate consistent id for new university
        uniId = `uni_custom_${Date.now()}`;
      }

      const input: CreateStudentDemandInput = {
        studentName: studentName.trim(),
        phone: phone.trim(),
        areaId: isCustomArea ? `custom_${Date.now()}` : selectedAreaId,
        areaName: isCustomArea ? customAreaName.trim() : undefined,
        areaGroupId: isCustomArea ? customAreaGroup : undefined,
        areaGroupName: isCustomArea ? areaGroupObj?.name : undefined,
        universityId: uniId,
        universityName: trimmedUniName,
        universityGovernorate: gov,
        attendanceDaysPerMonth: attendanceDays.trim() ? Number(attendanceDays) : null,
        preferredTravelDays: selectedDays.length > 0 ? selectedDays : undefined,
        preferredDepartureTime: departureTime.trim() || undefined,
        preferredReturnTime: returnTime.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      const creatorUser = {
        uid: user?.uid || 'student_guest',
        email: user?.email || studentProfile?.email || 'student@nashgo.com',
        name: studentName.trim() || studentProfile?.fullName || 'طالب مسجل',
      };

      const res = await studentDemandService.createDemandRecord(input, creatorUser, {
        skipDuplicateCheck: forceDuplicate,
      });

      if (!res.success) {
        if (res.duplicateFound && res.existingRecord) {
          setDuplicateWarning(res.existingRecord);
          setIsSubmitting(false);
          return;
        }
        setClientErrors({ general: res.error || 'فشل حفظ الطلب' });
        setIsSubmitting(false);
        return;
      }

      if (res.data) {
        onRecordCreated(res.data);
        setSuccessBanner(`تم تسجيل رغبة الطالب (${res.data.studentName}) بنجاح`);

        if (addAnother) {
          resetStudentFields();
          setTimeout(() => setSuccessBanner(null), 3000);
        } else {
          setTimeout(() => {
            onClose();
          }, 800);
        }
      }
    } catch (err: any) {
      setClientErrors({ general: err.message || 'حدث خطأ أثناء حفظ الطلب' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="demand-modal-overlay" onClick={onClose}>
      <div className="demand-modal-box" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="demand-modal-header">
          <div>
            <h3 className="demand-modal-title">
              <UserPlus size={22} className="text-blue-600" />
              <span>تسجيل رغبة طالب جديدة (Data Entry)</span>
            </h3>
            <span className="text-xs text-slate-500">
              إدخال رغبة انتقال طالب لتقييم جدوى فتح خطوط جامعية
            </span>
          </div>

          <button
            type="button"
            className="demand-modal-close-btn"
            onClick={onClose}
            title="إغلاق"
          >
            ✕
          </button>
        </div>

        {/* Success Alert */}
        {successBanner && (
          <div className="p-3 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-xl mb-3 text-xs font-bold flex items-center gap-2">
            <CheckCircle2 size={16} />
            <span>{successBanner}</span>
          </div>
        )}

        {/* Duplicate Warning Dialog */}
        {duplicateWarning && (
          <div className="duplicate-alert-box">
            <div className="duplicate-alert-title">
              <AlertTriangle size={18} className="text-amber-600" />
              <span>تنبيه: هذا الطالب مسجل بالفعل لهذه الجامعة!</span>
            </div>
            <p className="text-xs text-amber-900 mb-3 leading-relaxed">
              يوجد سجل نشط برقم الهاتف (<strong>{duplicateWarning.phone}</strong>) باسم (
              <strong>{duplicateWarning.studentName}</strong>) مسجل لـ (
              <strong>{duplicateWarning.universityNameSnapshot}</strong>) بمنطقة (
              <strong>{duplicateWarning.areaName}</strong>).
            </p>

            <div className="flex items-center gap-2 flex-wrap">
              {onOpenExistingRecord && (
                <button
                  type="button"
                  className="btn-primary text-xs py-1.5 px-3"
                  onClick={() => {
                    onOpenExistingRecord(duplicateWarning);
                    onClose();
                  }}
                >
                  فتح السجل الحالي
                </button>
              )}
              <button
                type="button"
                className="btn-admin-outline text-xs py-1.5 px-3"
                onClick={() => handleSubmit(false, true)}
                disabled={isSubmitting}
              >
                تأكيد التسجيل وتجاهل التكرار
              </button>
              <button
                type="button"
                className="btn-admin-danger text-xs py-1.5 px-3"
                onClick={() => setDuplicateWarning(null)}
              >
                إلغاء
              </button>
            </div>
          </div>
        )}

        {clientErrors.general && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl mb-3 text-xs font-bold">
            {clientErrors.general}
          </div>
        )}

        {/* Sticky Controls Pill */}
        <div className="demand-sticky-box">
          <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-700">
            <input
              type="checkbox"
              checked={keepSelectionsSticky}
              onChange={(e) => setKeepSelectionsSticky(e.target.checked)}
              className="accent-blue-600"
            />
            <span>تثبيت الجامعة والمنطقة لتسريع الإدخال المتكرر</span>
          </label>

          <button
            type="button"
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 font-semibold"
            onClick={resetStudentFields}
            title="تفريغ الحقول الشخصية"
          >
            <RotateCcw size={12} />
            <span>تفريغ الحقول</span>
          </button>
        </div>

        {/* Form Body */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(false);
          }}
        >
          <div className="demand-form-grid">
            {/* 1. Student Name */}
            <div className="demand-form-group">
              <label className="demand-form-label">
                <span>اسم الطالب</span>
                <span className="req">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                placeholder="مثال: أحمد محمد علي"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                className={`demand-form-input ${clientErrors.studentName ? 'border-red-400' : ''}`}
                required
              />
              {clientErrors.studentName && (
                <span className="demand-form-error">{clientErrors.studentName}</span>
              )}
            </div>

            {/* 2. Phone Number */}
            <div className="demand-form-group">
              <label className="demand-form-label">
                <span>رقم الهاتف المحمول</span>
                <span className="req">*</span>
              </label>
              <div className="relative">
                <input
                  type="tel"
                  dir="ltr"
                  placeholder="010xxxxxxxx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`demand-form-input ${clientErrors.phone ? 'border-red-400' : ''}`}
                  required
                />
                <Phone
                  size={14}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>
              <span className="demand-form-hint">رقم مصري مكون من 11 رقماً</span>
              {clientErrors.phone && (
                <span className="demand-form-error">{clientErrors.phone}</span>
              )}
            </div>

            {/* 3. University Input & Autocomplete */}
            <div className="demand-form-group">
              <label className="demand-form-label">
                <Building2 size={14} className="text-blue-600" />
                <span>اسم الجامعة المطلوبة</span>
                <span className="req">*</span>
              </label>

              <div className="uni-autocomplete-container" ref={uniDropdownRef}>
                <input
                  type="text"
                  placeholder="اكتب اسم الجامعة (مثال: جامعة القاهرة، جامعة المنصورة...)"
                  value={universityInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setUniversityInput(val);
                    setShowUniDropdown(true);

                    // Check if exact match exists in known universities
                    const match = knownUniversities.find(
                      (u) => u.name.trim().toLowerCase() === val.trim().toLowerCase()
                    );
                    if (match) {
                      setSelectedUniversityId(match.id);
                      setUniversityGovernorate(match.governorate);
                    } else {
                      setSelectedUniversityId('');
                    }
                  }}
                  onFocus={() => setShowUniDropdown(true)}
                  className={`demand-form-input font-bold ${
                    clientErrors.university ? 'border-red-400' : ''
                  }`}
                  required
                />

                {/* Suggestions Dropdown */}
                {showUniDropdown && (
                  <ul className="uni-suggestions-dropdown">
                    {filteredUniversities.length > 0 ? (
                      filteredUniversities.map((u) => (
                        <li
                          key={u.id}
                          className="uni-suggestion-item"
                          onClick={() => {
                            setUniversityInput(u.name);
                            setSelectedUniversityId(u.id);
                            setUniversityGovernorate(u.governorate);
                            setShowUniDropdown(false);
                          }}
                        >
                          <span className="uni-suggestion-name">{u.name}</span>
                          <span className="uni-suggestion-gov">{u.governorate}</span>
                        </li>
                      ))
                    ) : (
                      <li className="p-2 text-xs text-slate-500 text-center">
                        جامعة جديدة — يمكنك إكمال كتابة الاسم وتحديد المحافظة بالأسفل لإضافتها
                      </li>
                    )}
                  </ul>
                )}
              </div>

              <span className="demand-form-hint">
                اكتب اسم الجامعة، إذا كانت مسجلة مسبقاً ستظهر في القائمة أدناه، أو اكتب اسماً جديداً تماماً
              </span>
              {clientErrors.university && (
                <span className="demand-form-error">{clientErrors.university}</span>
              )}
            </div>

            {/* 4. University Governorate */}
            <div className="demand-form-group">
              <label className="demand-form-label">
                <span>محافظة الجامعة</span>
                <span className="req">*</span>
              </label>
              <input
                type="text"
                placeholder="مثال: الجيزة، القاهرة، الغربية..."
                value={universityGovernorate}
                onChange={(e) => setUniversityGovernorate(e.target.value)}
                className="demand-form-input font-bold"
              />
              <span className="demand-form-hint">
                {exactUniMatch
                  ? 'تم اشتقاقها تلقائياً من الجامعة المحددة'
                  : 'حدد المحافظة التي تقع فيها هذه الجامعة الجديدة'}
              </span>
            </div>

            {/* 5. Residential Area (Alexandria) */}
            <div className="demand-form-group full-width">
              <div className="flex justify-between items-center mb-1">
                <label className="demand-form-label">
                  <MapPin size={14} className="text-emerald-600" />
                  <span>المنطقة السكنية بالإسكندرية</span>
                  <span className="req">*</span>
                </label>

                <button
                  type="button"
                  className="text-xs text-blue-600 font-bold hover:underline"
                  onClick={() => setIsCustomArea(!isCustomArea)}
                >
                  {isCustomArea ? '← اختيار من المناطق المحددة مسبقاً' : '+ منطقة أخرى'}
                </button>
              </div>

              {!isCustomArea ? (
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="تصفية المناطق (مثال: سموحة، سيدي جابر، ميامي...)"
                      value={areaSearch}
                      onChange={(e) => setAreaSearch(e.target.value)}
                      className="demand-form-input text-xs py-1.5"
                    />
                  </div>

                  <select
                    value={selectedAreaId}
                    onChange={(e) => setSelectedAreaId(e.target.value)}
                    className="demand-form-select"
                  >
                    {filteredAreas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} — ({a.groupName})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-slate-50 p-3 rounded-xl border">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      اسم المنطقة السكنية
                    </label>
                    <input
                      type="text"
                      placeholder="اكتب اسم المنطقة..."
                      value={customAreaName}
                      onChange={(e) => setCustomAreaName(e.target.value)}
                      className="demand-form-input text-xs"
                      required
                    />
                    {clientErrors.customArea && (
                      <span className="demand-form-error">{clientErrors.customArea}</span>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      القطاع الجغرافي التابع له
                    </label>
                    <select
                      value={customAreaGroup}
                      onChange={(e) => setCustomAreaGroup(e.target.value)}
                      className="demand-form-select text-xs"
                    >
                      {ALEXANDRIA_AREA_GROUPS.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* 6. Attendance Days per Month (Optional) */}
            <div className="demand-form-group">
              <label className="demand-form-label">
                <Calendar size={14} className="text-purple-600" />
                <span>أيام الحضور شهرياً (اختياري)</span>
              </label>
              <input
                type="number"
                min={1}
                max={31}
                placeholder="مثال: 16"
                value={attendanceDays}
                onChange={(e) => setAttendanceDays(e.target.value)}
                className="demand-form-input font-mono"
              />
              <span className="demand-form-hint">
                يستخدم لحساب حجم الحضور الإجمالي (أيام-طالب)
              </span>
              {clientErrors.attendanceDays && (
                <span className="demand-form-error">{clientErrors.attendanceDays}</span>
              )}
            </div>

            {/* 6.1 Preferred Travel Days of Week */}
            <div className="demand-form-group full-width">
              <div className="flex justify-between items-center mb-1">
                <label className="demand-form-label">
                  <CalendarDays size={14} className="text-blue-600" />
                  <span>أيام السفر الأسبوعية للجامعة (محددات الجدول)</span>
                </label>
                <span className="text-xs text-slate-500 font-bold">
                  {selectedDays.length} {selectedDays.length === 1 ? 'يوم محدد' : 'أيام محددة'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                حدد أيام الأسبوع التي يحتاج الطالب السفر فيها لتوجيه تشغيل السيارات ومعرفة أعداد الركاب في كل يوم:
              </p>
              <div className="weekday-chips-grid">
                {WEEKDAY_OPTIONS.map((dayOpt) => {
                  const isSelected = selectedDays.includes(dayOpt.id);
                  return (
                    <button
                      key={dayOpt.id}
                      type="button"
                      className={`weekday-chip ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleTravelDay(dayOpt.id)}
                    >
                      <span>{isSelected ? '✓' : '+'}</span>
                      <span>{dayOpt.labelAr}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 7. Preferred Departure Time */}
            <div className="demand-form-group">
              <label className="demand-form-label">
                <span>الموعد الصباحي المفضل (ذهاب)</span>
              </label>
              <input
                type="time"
                value={departureTime}
                onChange={(e) => setDepartureTime(e.target.value)}
                className="demand-form-input font-mono"
              />
            </div>

            {/* 7.1 Preferred Return Time */}
            <div className="demand-form-group">
              <label className="demand-form-label">
                <span>موعد العودة المفضل (اختياري)</span>
              </label>
              <input
                type="time"
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
                className="demand-form-input font-mono"
              />
            </div>

            {/* 8. Notes */}
            <div className="demand-form-group full-width">
              <label className="demand-form-label">ملاحظات إضافية</label>
              <textarea
                rows={2}
                placeholder="مثال: كلية الهندسة، يرغب بالانطلاق من أمام محطة سيدي جابر..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="demand-form-textarea"
              />
            </div>
          </div>

          {/* Modal Footer Buttons */}
          <div className="demand-modal-actions">
            <button
              type="button"
              className="btn-admin-outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              إلغاء
            </button>

            <button
              type="button"
              className="btn-admin-action-primary py-2 px-4 text-xs font-bold"
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting}
              title="حفظ السجل الحالي مع تفريغ الحقول لإضافة طالب آخر فوراً"
            >
              <Sparkles size={14} />
              <span>حفظ وإضافة طالب آخر</span>
            </button>

            <button
              type="submit"
              className="btn-primary py-2 px-5 font-bold"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'جارٍ الحفظ...' : 'حفظ الطلب'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
